import React from 'react';
import { currencyChar } from 'services/utils';
import './Stats.scss';

/**
 * AvailableBalance Component
 *
 * Displays available balance or total spent information for consortia dashboards.
 * This component handles complex display logic including currency formatting,
 * split views for Open Access vs Online Open, and conditional rendering based
 * on configuration settings.
 *
 * Display Logic:
 * - Widget displays only when ALL of the following conditions are met:
 *   1. showAccountSummary = true
 *   2. capEnabled ≠ "No"
 *   3. capEnabled ≠ "Unlimited"
 *
 * Currency Display:
 * - Shows currency symbols ($, €, £) ONLY when capType = "Amount Spent"
 * - No currency symbols when capType = "Article Count"
 *
 * Open Access / Online Open Split:
 * - Split displays ONLY when:
 *   - revenueModel = "Both" AND
 *   - capConfig = "Separate Cap"
 * - When split displayed:
 *   - Top box shows "-" (dash) with no amount/count value
 *   - Split boxes below show individual Open Access and Online Open values
 *
 * Available Balance vs Total Spent:
 * - "Available Balance" label when showTotalSpent = false
 * - "Total Spent" label when showTotalSpent = true
 *
 * Reference: WOAA-2523 - Available Balance - Consortia Dashboard (AI-PDLC)
 */

interface AvailableBalanceProps {
  ConsortiaStats: ConsortiaStats;
  currency?: string;
  activeState?: boolean;
}

const AvailableBalance: React.FC<AvailableBalanceProps> = ({
  ConsortiaStats,
  activeState = true,
  currency = 'USD',
}) => {
  const {
    availableBalance,
    availableBalanceOnlineOpen,
    availableBalanceOpenAccess,
    capEnableAtThisLevel,
    capEnabled,
    capTypeCombined,
    capTypeGoa,
    capTypeHoa,
    onlineOpenAvailableBalance,
    openAccessAvailableBalance,
    revenueModel,
    showAccountSummary,
    showSummary,
    totalAmountSpent,
    totalArticleCount,
    totalOnlineOpenAmountSpent,
    totalOnlineOpenArticleCount,
    totalOpenAccessAmountSpent,
    totalOpenAccessArticleCount,
  } = ConsortiaStats;

  // Rule 1: Display Logic
  // Available balance displays only when ALL of the following conditions are met:
  // - Show Summary Config: Set to "Yes"
  // - Cap Enabled Config: Set to any value EXCEPT "No" or "Unlimited"
  // - Cap Type Config: Must be defined when Cap Enabled is NOT "No" or "Unlimited"
  const showSummaryFlag = showAccountSummary !== undefined ? showAccountSummary : showSummary;
  const capEnabledUpper = capEnabled?.toUpperCase();
  const shouldDisplay = showSummaryFlag === true && capEnabledUpper !== 'NO' && capEnabledUpper !== 'UNLIMITED';

  if (!shouldDisplay) {
    return null;
  }

  // Identify special configurations
  const isGoldOnly = capEnabledUpper === 'ENABLED_FOR_GOLD_OA' || capEnabledUpper === 'SHARING_ENABLED_FOR_GOLD_OA';
  const isHybridOnly =
    capEnabledUpper === 'ENABLED_FOR_HYBRID_OA' || capEnabledUpper === 'SHARING_ENABLED_FOR_HYBRID_OA';
  const isSeparateCap =
    capEnabledUpper === 'ENABLED_FOR_BOTH_SEPARATE_CAP' || capEnabledUpper === 'SHARING_ENABLED_FOR_BOTH_SEPARATE_CAP';

  // Rule 4: Available Balance vs Total Spent
  // Show "Available Balance" = Cap is enabled at the same level as the view (capEnableAtThisLevel = true)
  // Show "Total Spent" = Cap is enabled at a parent level or different level (capEnableAtThisLevel = false)
  const isShowingAvailableBalance = capEnableAtThisLevel && activeState;
  const label = isShowingAvailableBalance ? 'Available Balance' : 'Total Spent';

  // Rule 3: OA Split Display Rule
  // Split displays in the following cases:
  // - Total Spent view (except for Gold/Hybrid only configurations)
  // - Explicit separate cap configuration
  const shouldShowSplit = isSeparateCap || (!isShowingAvailableBalance && !isGoldOnly && !isHybridOnly);

  // Format currency with thousand separators and 2 decimal places
  const formatCurrency = (value: number): string => {
    const symbol = currencyChar(currency);
    // toLocaleString ensures thousand separators, toFixed(2) ensures 2 decimal places
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${symbol}${formatted}`;
  };

  // Rule 2: Currency Display Rule
  // Show currency symbol = capType is "Amount Spent"
  // No currency symbol = capType is "Article Count"
  const formatValue = (value: number | undefined, capType: string | undefined): string => {
    if (value === undefined) {
      return '-';
    }

    const capTypeUpper = capType?.toUpperCase();
    const shouldShowCurrency = capTypeUpper === 'AMOUNT_SPENT';

    if (shouldShowCurrency) {
      return formatCurrency(value);
    }

    return value.toString();
  };

  // Main display value (top box)
  let mainValue: string;
  let mainOpenAccessValue: number | undefined;
  let mainOnlineOpenValue: number | undefined;

  // Determine which cap type to use for the top box
  const currentCapType = (isGoldOnly ? capTypeGoa : isHybridOnly ? capTypeHoa : capTypeCombined) || capTypeCombined;

  if (isShowingAvailableBalance) {
    mainValue = shouldShowSplit ? '-' : formatValue(availableBalance, currentCapType);
    // Use mapped field names (availableBalanceOpenAccess) or fall back to API field names (openAccessAvailableBalance)
    mainOpenAccessValue =
      availableBalanceOpenAccess !== undefined ? availableBalanceOpenAccess : openAccessAvailableBalance;
    mainOnlineOpenValue =
      availableBalanceOnlineOpen !== undefined ? availableBalanceOnlineOpen : onlineOpenAvailableBalance;
  } else {
    // Determine which total spent value to show in the top box
    let currentTotalSpent = totalAmountSpent;
    if (isGoldOnly) currentTotalSpent = totalOpenAccessAmountSpent;
    else if (isHybridOnly) currentTotalSpent = totalOnlineOpenAmountSpent;

    if (currentCapType === 'ARTICLE_COUNT') {
      mainValue = totalArticleCount?.toString() ?? '-';
      mainOpenAccessValue = totalOpenAccessArticleCount;
      mainOnlineOpenValue = totalOnlineOpenArticleCount;
    } else {
      mainValue = formatValue(currentTotalSpent, currentCapType);
      mainOpenAccessValue = totalOpenAccessAmountSpent;
      mainOnlineOpenValue = totalOnlineOpenAmountSpent;
    }
  }

  return (
    <article
      className="stats"
      data-seleniumid="consortia-available-balance-widget"
      data-testid="available-balance-widget"
    >
      <header className="stats-header" data-seleniumid="consortia-available-balance-header">
        <p className="stats-totalAmount" data-seleniumid="consortia-available-balance">
          {mainValue}
        </p>
        <h4 className="stats-heading" data-seleniumid="consortia-available-balance-label">
          {label}
        </h4>
      </header>

      {shouldShowSplit && (
        <div className="stats-dataWrapper" data-seleniumid="consortia-available-balance-split-wrapper">
          <section className="stats-dataContainer" data-seleniumid="consortia-available-balance-oa-container">
            <h5 className="stats-dataHeading" data-seleniumid="consortia-available-balance-oa-label">
              Open Access
            </h5>
            <p className="stats-dataValue" data-seleniumid="consortia-available-balance-oa">
              {formatValue(mainOpenAccessValue, capTypeGoa)}
            </p>
          </section>
          <section className="stats-dataContainer" data-seleniumid="consortia-available-balance-oo-container">
            <h5 className="stats-dataHeading" data-seleniumid="consortia-available-balance-oo-label">
              Online Open
            </h5>
            <p className="stats-dataValue" data-seleniumid="consortia-available-balance-oo">
              {formatValue(mainOnlineOpenValue, capTypeHoa)}
            </p>
          </section>
        </div>
      )}
    </article>
  );
};

export default React.memo(AvailableBalance);
